import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import popupImage from "../images/popup.png";
import "./FindSkinSensitivity.css";

// convenience alias for non-JSX rendering
const h = React.createElement;

const FindSkinSensitivity = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Data & UI state
  const [questions] = useState(location.state?.questions || []);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [openQuestions, setOpenQuestions] = useState([]);
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const fromProfile = location.state?.fromProfile || false;

  // Open all questions on load
  useEffect(() => {
    if (questions && questions.length) {
      setOpenQuestions(questions.map((q) => q.id));
    }
  }, [questions]);

  // Enable Complete button when all answered
  const allQuestionsAnswered = questions.every((q) =>
    Object.prototype.hasOwnProperty.call(selectedAnswers, q.id)
  );

  // Select an answer -> record + collapse that question
  const handleAnswerSelect = (questionId, answerId) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerId }));
    setOpenQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  // Toggle a question card open/closed
  const toggleQuestion = (questionId) => {
    setOpenQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Submit answers -> verify sensitivity -> show popup
  const submitQAFormResults = async () => {
    try {
      const token = localStorage.getItem("token");
      const selectedAnswerIds = Object.values(selectedAnswers);

      const response = await fetch(
        "https://dev-api.curu.com.au/api/skin-sensitive/verify",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ answers: selectedAnswerIds }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const isSensitive = !!result.sensitive;
        const sName = isSensitive ? "Sensitive" : "Not Sensitive";
        const sId = isSensitive ? 1 : 2;

        setPopupMessage(
          '<div class="popup-result-skin-sensitivity-QA">' +
            sName +
            "</div>" +
            '<div class="popup-additional-skin-sensitivity-QA">' +
            (result.quiz_text || "") +
            "</div>"
        );

        // Persist result
        sessionStorage.setItem(
          "skin-sensitivity",
          JSON.stringify({ id: sId, name: sName })
        );
      } else {
        setPopupMessage(
          '<div class="popup-result-skin-sensitivity-QA">Error</div>' +
            '<div class="popup-additional-skin-sensitivity-QA">Failed to submit answers.</div>'
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error.message);
      setPopupMessage(
        '<div class="popup-result-skin-sensitivity-QA">Error</div>' +
          '<div class="popup-additional-skin-sensitivity-QA">An error occurred while submitting your answers.</div>'
      );
    }

    setShowPopup(true);
  };

  // Nav handlers
  const handleBackClick = () => {
    navigate("/skin-sensitivity");
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (fromProfile) {
      navigate("/user-profile");
    } else {
      navigate("/select-skintone");
    }
  };

  // ----- Render helpers (no JSX) -----
  const renderAnswer = (question, answer) =>
    h(
      "li",
      { className: "answer-option-skin-sensitivity-QA", key: answer.id },
      h(
        "label",
        null,
        h("input", {
          type: "radio",
          name: "question-" + question.id,
          value: answer.id,
          checked: selectedAnswers[question.id] === answer.id,
          onChange: function () {
            handleAnswerSelect(question.id, answer.id);
          },
        }),
        " ",
        answer.text
      )
    );

  const renderQuestion = (question) => {
    const isOpen = openQuestions.includes(question.id);
    const isAnswered = selectedAnswers[question.id] != null;

    return h(
      "div",
      {
        className: "section-skin-sensitivity-QA " + (isOpen ? "" : "folded"),
        key: question.id,
        "data-id": question.id,
      },
      // Header: toggles open/closed
      h(
        "div",
        {
          className: "question-header-skin-sensitivity-QA",
          onClick: function () {
            toggleQuestion(question.id);
          },
        },
        h(
          "h6",
          { className: "question-skin-sensitivity-QA" },
          question.text
        ),
        !isOpen &&
          isAnswered &&
          h(
            "div",
            { className: "custom-tickmark-container-skin-sensitivity-QA" },
            h("div", { className: "custom-tickmark-skin-sensitivity-QA" })
          ),
        
      ),

      // Answers (only when open)
      isOpen &&
        h(
          "ul",
          { className: "answer-list-skin-sensitivity-QA" },
          question.answers.map((answer) => renderAnswer(question, answer))
        )
    );
  };

  return h(
    "div",
    { className: "background-container-skin-sensitivity-QA" },
    h(
      "div",
      { className: "qa-form-container-skin-sensitivity-QA" },
      h(
        "h1",
        { className: "title-skin-sensitivity-QA" },
        "Find Your Skin Sensitivity"
      ),
      questions.length > 0
        ? h(
            "div",
            null,
            questions.map((q) => renderQuestion(q)),
            h(
              "div",
              { className: "buttons-container-skin-sensitivity-QA" },
              !fromProfile &&
                h(
                  "button",
                  {
                    className: "back-button-skin-sensitivity-QA",
                    onClick: handleBackClick,
                  },
                  "Back"
                ),
              h(
                "button",
                {
                  className:
                    "btn-complete-skin-sensitivity-QA " +
                    (!allQuestionsAnswered ? "disabled" : ""),
                  onClick: allQuestionsAnswered ? submitQAFormResults : undefined,
                  disabled: !allQuestionsAnswered,
                },
                "Complete"
              )
            )
          )
        : h("p", null, "No questions available")
    ),

    showPopup &&
      h(
        "div",
        { className: "popup-overlay-skin-sensitivity-QA" },
        h(
          "div",
          { className: "popup-content-skin-sensitivity-QA" },
          h("img", {
            src: popupImage,
            alt: "User",
            className: "popup-image-skin-sensitivity-QA",
          }),
          h("div", {
            dangerouslySetInnerHTML: { __html: popupMessage },
          }),
          h("button", { onClick: handleClosePopup }, "Close")
        )
      )
  );
};

export default FindSkinSensitivity;
